import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Send, CheckCircle, AlertCircle } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const RequestsPage = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // "success" | "error" | null
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/kolanut-landing/requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          email: email.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus("error");
        setMessage(data.message || data.error || "Something went wrong.");
        return;
      }
      setStatus("success");
      setMessage("Thanks! Your feature request has been submitted.");
      setTitle("");
      setDescription("");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(err.message || "Could not reach the server. Is the backend running?");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header />
      <main className="flex-grow py-12 md:py-20 px-4">
        <div className="max-w-xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            Feature request
          </h1>
          <p className="text-gray-600 mb-8">
            Tell us what you’d like to see next. We read every submission.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1.5">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                id="title"
                type="text"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Short summary of the feature"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#c4ffa0] focus:border-[#2d4a1e] outline-none transition"
              />
            </div>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                required
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the feature and how it would help you"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#c4ffa0] focus:border-[#2d4a1e] outline-none transition resize-y"
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email (optional)
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#c4ffa0] focus:border-[#2d4a1e] outline-none transition"
              />
            </div>

            {status && (
              <div
                className={`flex items-center gap-2 px-4 py-3 rounded-xl ${
                  status === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {status === "success" ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span className="text-sm font-medium">{message}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#2d4a1e] text-white font-semibold rounded-xl hover:bg-[#2d4a1e]/90 focus:ring-2 focus:ring-[#c4ffa0] focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {submitting ? (
                "Submitting…"
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit request
                </>
              )}
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RequestsPage;
